/**
 * The builtin sheets' product shape (`:journal` today): restoring a promoted
 * builtin's reserved keys, timescale, width and reserved rows at the parse
 * boundary, and seeding the reserved rows a sheet opened by key is missing.
 * Product policy for the builtin catalog, kept out of the client's
 * composition root; stateless.
 */
import type { Option, UserId } from "@tento-lona/sheets";
import { BUILTIN_SHEET_CATALOG, isBuiltinSheetKey, Json } from "@tento-lona/sheets";
import type { Network } from "../network.ts";
import type { RowNode } from "../rows/row-node.ts";
import { EphemeralRowSeeder } from "./ephemeral-row-seeder.ts";

/** The parts of a sheet entry (`SheetHandle.ParseInput`) a builtin's shape is read from and restored on. */
export type SeedableSheet = Pick<Network.UserSheet, "lookupKey" | "timeScale" | "width" | "title"> & {
  readonly rows: readonly RowNode.DecomposableRow[];
};

/**
 * Restore a builtin sheet's SHAPE at the PARSE boundary — the one point every
 * ingestion path shares (`SheetHandle.parse`), not just `sheets.open(key)`.
 *
 * A promoted builtin can come back from the server having lost the metadata
 * that makes it that builtin: the sheet's reserved `lookupKey` and `timeScale`,
 * its `width`, and its reserved rows' keys / heights. This re-derives the key
 * (from the data, the caller's open key, or the authored shape), stamps it back
 * on, and refills what the key implies.
 *
 * Stamping `lookupKey` is load-bearing beyond row shape: `SheetViewModel.make`
 * resolves the view config with `resolveForSheet(timeScale, sheet.lookupKey)`,
 * and the Journal's day-count default (3) is keyed on `:journal` — a null key
 * silently falls back to the generic 7-day default, which is how a cold load
 * rendered "7 Days" where a client-side navigation rendered the journal view.
 */
export function restoreBuiltinSheetShape<T extends SeedableSheet>(
  data: T,
  fallbackSheetLookupKey: Option<string>,
): T {
  const sheetLookupKey = builtinSheetLookupKeyForSeed(
    data,
    fallbackSheetLookupKey,
  );
  if (sheetLookupKey == null) return data;

  let changed = false;
  // The reserved key IS the sheet's identity. Re-deriving it but leaving it off
  // the parsed sheet would repair the rows and still hand the view-config
  // resolution a null key.
  if (data.lookupKey !== sheetLookupKey) changed = true;
  // Sheet timescale falls back to the builtin's authored default — the catalog
  // is the source of truth for it, and `SheetViewModel.make` otherwise defaults
  // a keyless sheet to "week".
  const catalogTimescale = BUILTIN_SHEET_CATALOG[sheetLookupKey]?.timeScale ??
    null;
  const timeScale = data.timeScale ?? catalogTimescale;
  if (data.timeScale == null && timeScale != null) changed = true;
  const width = sheetLookupKey === JOURNAL_SHEET_LOOKUP_KEY
    ? data.width ?? JOURNAL_SHEET_WIDTH_PX
    : data.width;
  if (sheetLookupKey === JOURNAL_SHEET_LOOKUP_KEY && data.width == null) {
    changed = true;
  }
  let rows = data.rows;
  if (sheetLookupKey === JOURNAL_SHEET_LOOKUP_KEY) {
    const normalized = normalizeJournalBuiltinRows(data.rows);
    rows = normalized.rows;
    changed = changed || normalized.changed;
  }
  if (!changed) return data;

  return { ...data, lookupKey: sheetLookupKey, timeScale, width, rows };
}

/**
 * `restoreBuiltinSheetShape` PLUS the builtin's missing reserved rows.
 *
 * Seeding is deliberately NOT part of the parse-boundary restoration: a caller
 * that hands `parse` an explicit sheet (template, `?e=` blob, embed preview)
 * has said exactly which rows the sheet has, and inventing more there changed
 * what a promote POSTs. This variant is for the paths that materialize a sheet
 * FROM A KEY — `sheets.open(key)` — where the reserved rows are expected to
 * exist and their absence is a gap to fill, not an authoring choice.
 */
export function withSeededReservedRows<T extends SeedableSheet>(
  data: T,
  principal: UserId,
  fallbackSheetLookupKey: Option<string>,
): T {
  const restored = restoreBuiltinSheetShape(data, fallbackSheetLookupKey);
  const sheetLookupKey = builtinSheetLookupKeyForSeed(
    restored,
    fallbackSheetLookupKey,
  );
  if (sheetLookupKey == null) return restored;

  const forkedByLookupKey = new Set<string>();
  const presentSeedKeys = new Set<string>();
  for (const row of restored.rows) {
    if (row.lookupKey) forkedByLookupKey.add(row.lookupKey);
    const seedKey = seedKeyForBuiltinRow(sheetLookupKey, row);
    if (seedKey != null) presentSeedKeys.add(seedKey);
  }

  const seeded = EphemeralRowSeeder.make(
    sheetLookupKey,
    principal,
    forkedByLookupKey,
    presentSeedKeys,
  );
  if (seeded.length === 0) return restored;

  return { ...restored, rows: [...restored.rows, ...seeded] };
}

const JOURNAL_SHEET_LOOKUP_KEY = ":journal";
const JOURNAL_ROW_LOOKUP_KEY = ":~lona:journal";
const JOURNAL_ROW_LABEL = "Journal";
const JOURNAL_ROW_HEIGHT_PX = 400;
const JOURNAL_SHEET_WIDTH_PX = 300;
const JOURNAL_MOOD_SEED_KEY = "journal:mood";
const JOURNAL_MOOD_DEFAULT_ATTRIBUTES: {
  readonly unit: string;
  readonly prompt: string;
} = {
  unit: "scale1-5",
  prompt: "rate your mood (1-5)",
};

function builtinSheetLookupKeyForSeed(
  data: SeedableSheet,
  fallbackSheetLookupKey: Option<string>,
): Option<string> {
  if (isBuiltinSheetKey(data.lookupKey)) return data.lookupKey;
  if (isBuiltinSheetKey(fallbackSheetLookupKey)) return fallbackSheetLookupKey;
  if (data.title === "Journal" && hasJournalBuiltinRow(data.rows)) {
    return JOURNAL_SHEET_LOOKUP_KEY;
  }
  return null;
}

function hasJournalBuiltinRow(rows: readonly RowNode.DecomposableRow[]): boolean {
  for (const row of rows) {
    if (isJournalSourceSeedRow(row)) return true;
  }
  return false;
}

function normalizeJournalBuiltinRows(
  rows: readonly RowNode.DecomposableRow[],
): { rows: RowNode.DecomposableRow[]; changed: boolean } {
  const normalizedRows: RowNode.DecomposableRow[] = [];
  let changed = false;
  let hasJournal = false;
  let hasMood = false;

  for (const row of rows) {
    if (isJournalSourceSeedRow(row)) {
      if (hasJournal) {
        changed = true;
        continue;
      }
      hasJournal = true;
      const normalized = normalizeJournalBuiltinRow(row);
      if (normalized !== row) changed = true;
      normalizedRows.push(normalized);
      continue;
    }

    if (isJournalMoodSeedRow(row)) {
      if (hasMood) {
        changed = true;
        continue;
      }
      hasMood = true;
      const normalized = normalizeJournalBuiltinRow(row);
      if (normalized !== row) changed = true;
      normalizedRows.push(normalized);
      continue;
    }

    normalizedRows.push(row);
  }

  return { rows: normalizedRows, changed };
}

function normalizeJournalBuiltinRow(
  row: RowNode.DecomposableRow,
): RowNode.DecomposableRow {
  if (isJournalSourceSeedRow(row)) {
    if (
      row.lookupKey === JOURNAL_ROW_LOOKUP_KEY &&
      row.heightPx === JOURNAL_ROW_HEIGHT_PX &&
      row.timeScale === "day"
    ) {
      return row;
    }
    return {
      ...row,
      lookupKey: JOURNAL_ROW_LOOKUP_KEY,
      heightPx: JOURNAL_ROW_HEIGHT_PX,
      timeScale: "day",
    };
  }

  if (!isJournalMoodSeedRow(row)) return row;
  const attributes = Json.object(row.attributes ?? null);
  const hasCanonicalAttributes =
    attributes != null &&
    Json.isObject(attributes) &&
    attributes.unit === "scale1-5" &&
    typeof attributes.prompt === "string";
  if (row.timeScale === "day" && hasCanonicalAttributes) {
    return row;
  }
  return {
    ...row,
    timeScale: "day",
    attributes: {
      ...JOURNAL_MOOD_DEFAULT_ATTRIBUTES,
      ...(attributes != null && Json.isObject(attributes) ? attributes : {}),
      unit: "scale1-5",
    },
  };
}

function seedKeyForBuiltinRow(
  sheetLookupKey: string,
  row: RowNode.DecomposableRow,
): Option<string> {
  if (sheetLookupKey !== JOURNAL_SHEET_LOOKUP_KEY) return null;
  if (isJournalSourceSeedRow(row)) return JOURNAL_ROW_LOOKUP_KEY;
  if (isJournalMoodSeedRow(row)) return JOURNAL_MOOD_SEED_KEY;
  return null;
}

function isJournalSourceSeedRow(row: RowNode.DecomposableRow): boolean {
  if (row.lookupKey === JOURNAL_ROW_LOOKUP_KEY) return true;
  return row.lookupKey == null &&
    row.type === "journal" &&
    row.label === JOURNAL_ROW_LABEL;
}

function isJournalMoodSeedRow(row: RowNode.DecomposableRow): boolean {
  if (row.lookupKey != null) return false;
  return row.type === "number" && row.label === "Mood";
}
